/**
 * Structured day-list editor (setlist, guest list, contacts).
 * Stored as JSON: { v: 1, songs: [{ id, title, open, lines: [{ id, fields: [string] | [string, string] }] }] }
 */
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  SETLIST_LABELS,
  GUEST_LIST_LABELS,
  CONTACTS_LABELS,
  STRUCTURED_DAY_LIST_LABELS,
} from './structuredDayListLabels';

export function newSetlistSongId() {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function newSetlistLineId() {
  return `sl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function createEmptySong(open = true) {
  return { id: newSetlistSongId(), title: '', open, lines: [] };
}

function createEmptyLine() {
  return { id: newSetlistLineId(), fields: [''] };
}

function normalizeLine(row) {
  if (!row || typeof row !== 'object') return null;
  const id = String(row.id || '').trim() || newSetlistLineId();
  const rawFields = Array.isArray(row.fields) ? row.fields : [String(row.text ?? row.value ?? '')];
  const fields = rawFields.slice(0, 2).map((f) => String(f ?? ''));
  if (fields.length === 0) fields.push('');
  return { id, fields };
}

function normalizeBpmField(value) {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/[^\d.]/g, ''));
  if (!Number.isFinite(n) || n < 40 || n > 300) return null;
  return Math.round(n);
}

/** Song length as m:ss. Empty string clears it. null means the draft is not a length yet. */
export function normalizeSongDuration(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);
  if (!match) return null;
  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds) || seconds > 59 || minutes > 99) return null;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function normalizePending(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const songs = Array.isArray(raw.songs) ? raw.songs.map(normalizeSong).filter(Boolean) : null;
  if (!songs) return null;
  return {
    byId: String(raw.byId || raw.by || '').trim(),
    byName: String(raw.byName || '').trim(),
    at: String(raw.at || '').trim(),
    songs,
  };
}

function normalizeSong(row) {
  if (!row || typeof row !== 'object') return null;
  const id = String(row.id || '').trim() || newSetlistSongId();
  const title = String(row.title ?? row.name ?? row.song ?? '');
  const lines = (Array.isArray(row.lines) ? row.lines : [])
    .map(normalizeLine)
    .filter(Boolean);
  const bpm = normalizeBpmField(row.bpm);
  const duration = normalizeSongDuration(row.duration);
  return {
    id,
    title,
    open: row.open !== false,
    lines,
    ...(bpm != null ? { bpm } : {}),
    ...(duration ? { duration } : {}),
  };
}

export function parseSetlistText(raw) {
  const s = String(raw || '').trim();
  if (!s) return { songs: [], pending: null };
  try {
    const o = JSON.parse(s);
    if (o && o.v === 1 && (Array.isArray(o.songs) || Array.isArray(o.items))) {
      const rows = Array.isArray(o.songs) ? o.songs : o.items;
      return { songs: rows.map(normalizeSong).filter(Boolean), pending: normalizePending(o.pending) };
    }
    if (Array.isArray(o)) {
      return {
        songs: o.map((item) => {
          if (typeof item === 'string') {
            return normalizeSong({ id: newSetlistSongId(), title: item, open: false, lines: [] });
          }
          return normalizeSong(item);
        }).filter(Boolean),
        pending: null,
      };
    }
  } catch {
    /* fall through */
  }
  const lines = s.split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length) {
    return {
      songs: lines.map((title) => normalizeSong({ id: newSetlistSongId(), title, open: false, lines: [] })),
      pending: null,
    };
  }
  return { songs: [], pending: null };
}

export function serializeSetlistDoc(doc) {
  const songs = (Array.isArray(doc?.songs) ? doc.songs : [])
    .map((song) => {
      const title = String(song?.title || '').trim();
      const lines = (Array.isArray(song?.lines) ? song.lines : [])
        .map((line) => {
          const fields = (Array.isArray(line?.fields) ? line.fields : [])
            .slice(0, 2)
            .map((f) => String(f ?? '').trim())
            .filter((f, idx, arr) => {
              if (idx === 0) return true;
              return arr.some(Boolean);
            });
          while (fields.length < 1) fields.push('');
          if (fields.length === 2 && !fields[1]) fields.pop();
          return fields.some((f) => f.trim()) ? { id: String(line.id || newSetlistLineId()), fields } : null;
        })
        .filter(Boolean);
      if (!title && lines.length === 0) return null;
      const bpm = normalizeBpmField(song.bpm);
      const duration = normalizeSongDuration(song.duration);
      return {
        id: String(song.id || newSetlistSongId()),
        title,
        open: Boolean(song.open),
        lines,
        ...(bpm != null ? { bpm } : {}),
        ...(duration ? { duration } : {}),
      };
    })
    .filter(Boolean);
  if (songs.length === 0 && !doc?.pending) return '';
  const payload = { v: 1, songs };
  const pending = normalizePending(doc?.pending);
  if (pending) payload.pending = pending;
  return JSON.stringify(payload);
}

export function formatSetlistAsPlainText(raw, labels = SETLIST_LABELS) {
  const { songs } = parseSetlistText(raw);
  if (!songs.length) return '';
  const fallback = labels.itemFallback || 'Item';
  const parts = songs.map((song, index) => {
    const title = String(song.title || '').trim() || `${fallback} ${index + 1}`;
    const detailLines = (song.lines || [])
      .map((line) => {
        const fields = (line.fields || []).map((f) => String(f || '').trim()).filter(Boolean);
        if (!fields.length) return '';
        return fields.length === 1 ? `  ${fields[0]}` : `  ${fields[0]} — ${fields[1]}`;
      })
      .filter(Boolean);
    return [title, ...detailLines].join('\n');
  });
  return parts.join('\n\n');
}

function itemPreviewParts(song, index, labels) {
  const title = String(song?.title || '').trim();
  const firstLine = (song?.lines || [])
    .map((line) => (line.fields || []).map((f) => String(f || '').trim()).filter(Boolean).join(' — '))
    .find(Boolean);
  if (title) {
    return { title, subtitle: firstLine && firstLine !== title ? firstLine : '' };
  }
  if (firstLine) return { title: firstLine, subtitle: '' };
  return { title: labels.untitledItem || 'Untitled item', subtitle: '' };
}

function ItemDetailLinesEditor({ song, theme, styles, labels, onChangeLines }) {
  const lines = song.lines || [];

  const updateLineField = (lineId, fieldIndex, value) => {
    onChangeLines(
      lines.map((line) => {
        if (line.id !== lineId) return line;
        const fields = [...(line.fields || [''])];
        while (fields.length <= fieldIndex) fields.push('');
        fields[fieldIndex] = value;
        return { ...line, fields: fields.slice(0, 2) };
      })
    );
  };

  const expandLine = (lineId) => {
    onChangeLines(
      lines.map((line) => {
        if (line.id !== lineId) return line;
        const fields = [...(line.fields || [''])];
        if (fields.length < 2) fields.push('');
        return { ...line, fields: fields.slice(0, 2) };
      })
    );
  };

  const collapseLine = (lineId) => {
    onChangeLines(
      lines.map((line) => {
        if (line.id !== lineId) return line;
        return { ...line, fields: [String(line.fields?.[0] || '')] };
      })
    );
  };

  const removeLine = (lineId) => {
    onChangeLines(lines.filter((line) => line.id !== lineId));
  };

  const addLine = () => {
    onChangeLines([...lines, createEmptyLine()]);
  };

  return (
    <View style={styles.songDetailsBlock}>
      {lines.map((line) => {
        const fields = line.fields || [''];
        const hasSecond = fields.length >= 2;
        return (
          <View key={line.id} style={styles.detailLineRow}>
            <TextInput
              style={[styles.detailField, styles.detailFieldPrimary]}
              value={fields[0] || ''}
              onChangeText={(t) => updateLineField(line.id, 0, t)}
              placeholder={labels.detailPlaceholder}
              placeholderTextColor={theme.text.tertiary}
            />
            {hasSecond ? (
              <TextInput
                style={[styles.detailField, styles.detailFieldSecondary]}
                value={fields[1] || ''}
                onChangeText={(t) => updateLineField(line.id, 1, t)}
                placeholder={labels.secondFieldPlaceholder}
                placeholderTextColor={theme.text.tertiary}
              />
            ) : (
              <TouchableOpacity
                style={styles.detailExpandBtn}
                onPress={() => expandLine(line.id)}
                accessibilityLabel="Add second value on this line"
              >
                <Ionicons name="add" size={14} color={theme.brand.primary} />
              </TouchableOpacity>
            )}
            {hasSecond ? (
              <TouchableOpacity
                style={styles.detailCollapseBtn}
                onPress={() => collapseLine(line.id)}
                accessibilityLabel="Use one value on this line"
              >
                <Ionicons name="remove" size={14} color={theme.text.secondary} />
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={styles.detailRemoveBtn}
              onPress={() => removeLine(line.id)}
              accessibilityLabel="Remove detail line"
            >
              <Ionicons name="close" size={14} color={theme.text.secondary} />
            </TouchableOpacity>
          </View>
        );
      })}
      <TouchableOpacity style={styles.addDetailLineBtn} onPress={addLine}>
        <Ionicons name="add-circle-outline" size={15} color={theme.brand.primary} />
        <Text style={styles.addDetailLineText}>Add detail line</Text>
      </TouchableOpacity>
    </View>
  );
}

function useDebouncedCallback(fn, delayMs) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);
  const pendingRef = useRef(null);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pendingRef.current != null) fnRef.current(pendingRef.current);
    },
    []
  );
  return useCallback(
    (arg) => {
      pendingRef.current = arg;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        pendingRef.current = null;
        timerRef.current = null;
        fnRef.current(arg);
      }, delayMs);
    },
    [delayMs]
  );
}

function draftHasContent(title, lines) {
  if (String(title || '').trim()) return true;
  return (lines || []).some((line) => (line.fields || []).some((f) => String(f || '').trim()));
}

export function SetlistEditor({
  value,
  onChange,
  mode,
  theme,
  fullMaxHeight,
  compactFloat,
  compactFloatSide = 'right',
  compactFill = false,
  /** List grows with parent scroll (sidebar) — no nested max-height clip. */
  compactUnscroll = false,
  /** When true, full-mode list flows in a parent ScrollView (modals). */
  fullUnscroll = false,
  /** Compact + smaller box/text (used inside month-view day cells). */
  dense = false,
  labels = SETLIST_LABELS,
}) {
  const useFullInline = mode === 'full' || (mode === 'compact' && compactFill && !compactFloat && !compactUnscroll);
  const styles = useMemo(
    () =>
      createStyles(
        theme,
        mode === 'compact' && !useFullInline,
        fullMaxHeight,
        Boolean(compactFloat),
        compactFill,
        compactUnscroll,
        fullUnscroll,
        dense
      ),
    [theme, mode, fullMaxHeight, compactFloat, compactFill, compactUnscroll, useFullInline, fullUnscroll, dense]
  );
  const serializedProp = useMemo(() => String(value || ''), [value]);
  const lastEmittedRef = useRef(serializedProp);
  const [localSongs, setLocalSongs] = useState(() => parseSetlistText(value).songs);

  useEffect(() => {
    if (serializedProp !== lastEmittedRef.current) {
      setLocalSongs(parseSetlistText(value).songs);
      lastEmittedRef.current = serializedProp;
    }
  }, [serializedProp, value]);

  const debouncedOnChange = useDebouncedCallback(
    useCallback(
      (next) => {
        lastEmittedRef.current = next;
        onChange(next);
      },
      [onChange]
    ),
    500
  );

  const songs = localSongs;
  const isCompact = mode === 'compact' && !useFullInline;
  const [compactComposer, setCompactComposer] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftOpen, setDraftOpen] = useState(true);
  const [draftLines, setDraftLines] = useState([]);

  const pushSongs = useCallback(
    (nextSongs) => {
      setLocalSongs(nextSongs);
      debouncedOnChange(serializeSetlistDoc({ songs: nextSongs }));
    },
    [debouncedOnChange]
  );

  const resetComposerDraft = useCallback((song) => {
    if (song) {
      setDraftTitle(String(song.title || ''));
      setDraftOpen(song.open !== false);
      setDraftLines((song.lines || []).map((line) => ({
        id: line.id,
        fields: [...(line.fields || [''])],
      })));
      return;
    }
    setDraftTitle('');
    setDraftOpen(true);
    setDraftLines([]);
  }, []);

  const commitComposerDraft = useCallback(
    (keepOpen = false) => {
      if (!compactComposer) return false;
      const payload = {
        id: compactComposer.mode === 'edit' ? compactComposer.id : newSetlistSongId(),
        title: draftTitle,
        open: draftOpen,
        lines: draftLines.map((line) => ({
          id: line.id || newSetlistLineId(),
          fields: (line.fields || ['']).slice(0, 2),
        })),
      };
      const hasContent =
        String(payload.title || '').trim() ||
        payload.lines.some((line) => (line.fields || []).some((f) => String(f || '').trim()));
      if (!hasContent && compactComposer.mode === 'new') {
        if (!keepOpen) setCompactComposer(null);
        return false;
      }
      if (compactComposer.mode === 'edit') {
        pushSongs(songs.map((song) => (song.id === compactComposer.id ? payload : song)));
      } else {
        pushSongs([...songs, payload]);
      }
      if (keepOpen) {
        resetComposerDraft(null);
        setCompactComposer({ mode: 'new' });
      } else {
        setCompactComposer(null);
      }
      return true;
    },
    [compactComposer, draftTitle, draftOpen, draftLines, pushSongs, songs, resetComposerDraft]
  );

  useEffect(() => {
    if (!isCompact || !compactComposer || compactComposer.mode !== 'edit') return;
    if (!songs.some((song) => song.id === compactComposer.id)) {
      setCompactComposer(null);
    }
  }, [isCompact, compactComposer, songs]);

  if (isCompact) {
    const openNewComposer = () => {
      if (compactComposer && draftHasContent(draftTitle, draftLines)) {
        commitComposerDraft(true);
        return;
      }
      setCompactComposer({ mode: 'new' });
      resetComposerDraft(null);
    };
    const openEditComposer = (song) => {
      setCompactComposer({ mode: 'edit', id: song.id });
      resetComposerDraft(song);
    };

    const composerCard = dense ? (
      <View style={[styles.compactComposerCard, styles.denseComposerRow, compactFloat && styles.compactComposerCardFloating]}>
        <TextInput
          style={[styles.compactTitleInput, styles.denseComposerInput]}
          value={draftTitle}
          onChangeText={setDraftTitle}
          placeholder={labels.titlePlaceholder}
          placeholderTextColor={theme.text.tertiary}
          onSubmitEditing={() => commitComposerDraft(false)}
          returnKeyType="done"
        />
        {compactComposer?.mode === 'edit' ? (
          <TouchableOpacity
            style={styles.denseComposerIconBtn}
            onPress={() => {
              pushSongs(songs.filter((song) => song.id !== compactComposer.id));
              setCompactComposer(null);
            }}
            accessibilityLabel={labels.removeItemA11y}
          >
            <Ionicons name="trash-outline" size={14} color={theme.status?.error || '#c0392b'} />
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity style={styles.denseComposerIconBtn} onPress={() => setCompactComposer(null)} accessibilityLabel="Cancel">
          <Ionicons name="close" size={15} color={theme.text.secondary} />
        </TouchableOpacity>
        <TouchableOpacity style={[styles.denseComposerIconBtn, styles.denseComposerIconBtnPrimary]} onPress={() => commitComposerDraft(false)} accessibilityLabel="Save">
          <Ionicons name="checkmark" size={15} color={theme.button.primaryText} />
        </TouchableOpacity>
      </View>
    ) : (
      <View style={[styles.compactComposerCard, compactFloat && styles.compactComposerCardFloating]}>
        <TextInput
          style={styles.compactTitleInput}
          value={draftTitle}
          onChangeText={setDraftTitle}
          placeholder={labels.titlePlaceholder}
          placeholderTextColor={theme.text.tertiary}
        />
        <ItemDetailLinesEditor
          song={{ lines: draftLines }}
          theme={theme}
          styles={styles}
          labels={labels}
          onChangeLines={setDraftLines}
        />
        <View style={styles.compactComposerActions}>
          {compactComposer?.mode === 'edit' ? (
            <TouchableOpacity
              style={styles.compactComposerBtnDanger}
              onPress={() => {
                pushSongs(songs.filter((song) => song.id !== compactComposer.id));
                setCompactComposer(null);
              }}
            >
              <Text style={styles.compactComposerBtnDangerText}>Delete</Text>
            </TouchableOpacity>
          ) : null}
          <View style={styles.compactComposerActionsFlex} />
          <TouchableOpacity style={styles.compactComposerBtnGhost} onPress={() => setCompactComposer(null)}>
            <Text style={styles.compactComposerBtnGhostText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.compactComposerBtnPrimary} onPress={() => commitComposerDraft(false)}>
            <Text style={styles.compactComposerBtnPrimaryText}>Save</Text>
          </TouchableOpacity>
        </View>
      </View>
    );

    const renderCompactSongRows = () =>
      songs.map((song, index) => {
        const editingHere = compactComposer?.mode === 'edit' && compactComposer.id === song.id;
        const parts = editingHere
          ? itemPreviewParts({ title: draftTitle, lines: draftLines }, index, labels)
          : itemPreviewParts(song, index, labels);
        return (
          <View key={song.id} style={styles.compactRowWrap}>
            <TouchableOpacity
              style={[styles.compactRow, editingHere && styles.compactRowActive]}
              onPress={() => openEditComposer(song)}
              activeOpacity={0.85}
            >
              <View style={styles.compactIndexBadge}>
                <Text style={styles.compactIndexText}>{index + 1}</Text>
              </View>
              <View style={styles.compactRowBody}>
                <Text style={styles.compactPreview} numberOfLines={2}>
                  {parts.title}
                </Text>
                {parts.subtitle ? (
                  <Text style={styles.compactSubtitle} numberOfLines={1}>
                    {parts.subtitle}
                  </Text>
                ) : null}
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.compactDeleteBtn}
              onPress={() => pushSongs(songs.filter((s) => s.id !== song.id))}
              accessibilityLabel={labels.removeItemA11y}
              hitSlop={8}
            >
              <Ionicons name="close" size={16} color={theme.text.secondary} />
            </TouchableOpacity>
          </View>
        );
      });

    const listAddButton =
      compactComposer?.mode === 'new' ? null : (
        <TouchableOpacity
          style={styles.compactPlusBtnInline}
          onPress={openNewComposer}
          accessibilityLabel={labels.addItemA11y}
        >
          <Ionicons name="add" size={13} color={theme.brand.primary} />
        </TouchableOpacity>
      );

    const listBody = <>{renderCompactSongRows()}</>;

    const listScroll = compactUnscroll ? (
      <View style={styles.scrollCompactUnscroll}>{listBody}</View>
    ) : (
      <ScrollView
        style={styles.scrollCompact}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {listBody}
      </ScrollView>
    );

    const floatSideStyle =
      compactFloatSide === 'left' ? styles.compactComposerFloatLeft : styles.compactComposerFloatRight;

    if (compactFloat) {
      return (
        <View style={[styles.wrap, styles.compactFloatingHost]}>
          <View style={styles.compactFloatingAnchor}>
            {songs.length > 0 ? listScroll : null}
            {listAddButton}
          </View>
          {compactComposer ? (
            <View style={[styles.compactComposerFloat, floatSideStyle]}>{composerCard}</View>
          ) : null}
        </View>
      );
    }

    if (songs.length === 0 && !compactComposer) {
      return (
        <View style={styles.wrap}>
          <TouchableOpacity
            style={styles.compactPlusBtnInline}
            onPress={openNewComposer}
            accessibilityLabel={labels.addItemA11y}
          >
            <Ionicons name="add" size={13} color={theme.brand.primary} />
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={styles.wrap}>
        {songs.length > 0 ? listScroll : null}
        {listAddButton}
        {compactComposer ? composerCard : null}
      </View>
    );
  }

  const updateSong = (songId, patch) => {
    pushSongs(
      songs.map((song) => (song.id === songId ? { ...song, ...patch } : song))
    );
  };

  const removeSong = (songId) => {
    pushSongs(songs.filter((song) => song.id !== songId));
  };

  const moveSong = (index, delta) => {
    const j = index + delta;
    if (j < 0 || j >= songs.length) return;
    const next = [...songs];
    const tmp = next[index];
    next[index] = next[j];
    next[j] = tmp;
    pushSongs(next);
  };

  const renderFullSongCard = (song, index) => (
    <View key={song.id} style={styles.songCard}>
      <View style={styles.songHeaderRow}>
        <View style={styles.fullReorderCol}>
          <TouchableOpacity
            style={[styles.fullReorderBtn, index === 0 && styles.fullReorderBtnDisabled]}
            onPress={() => moveSong(index, -1)}
            disabled={index === 0}
            accessibilityLabel={labels.moveUpA11y}
          >
            <Ionicons name="chevron-up" size={16} color={theme.text.primary} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.fullReorderBtn, index === songs.length - 1 && styles.fullReorderBtnDisabled]}
            onPress={() => moveSong(index, 1)}
            disabled={index === songs.length - 1}
            accessibilityLabel={labels.moveDownA11y}
          >
            <Ionicons name="chevron-down" size={16} color={theme.text.primary} />
          </TouchableOpacity>
        </View>
        <TouchableOpacity
          style={styles.songToggleBtn}
          onPress={() => updateSong(song.id, { open: !song.open })}
          accessibilityLabel={song.open ? labels.collapseA11y : labels.expandA11y}
        >
          <Ionicons
            name={song.open ? 'chevron-down' : 'chevron-forward'}
            size={16}
            color={theme.text.primary}
          />
        </TouchableOpacity>
        <TextInput
          style={styles.songTitleInput}
          value={song.title}
          onChangeText={(t) => updateSong(song.id, { title: t })}
          placeholder={labels.titlePlaceholder}
          placeholderTextColor={theme.text.tertiary}
        />
        <TouchableOpacity
          style={styles.songDeleteBtn}
          onPress={() => removeSong(song.id)}
          accessibilityLabel={labels.removeItemA11y}
        >
          <Ionicons name="trash-outline" size={18} color={theme.status?.error || '#c0392b'} />
        </TouchableOpacity>
      </View>
      {song.open ? (
        <ItemDetailLinesEditor
          song={song}
          theme={theme}
          styles={styles}
          labels={labels}
          onChangeLines={(lines) => updateSong(song.id, { lines })}
        />
      ) : null}
    </View>
  );

  return (
    <View style={styles.wrap}>
      {fullUnscroll ? (
        <View style={styles.scrollFullUnscroll}>
          {songs.map((song, index) => renderFullSongCard(song, index))}
        </View>
      ) : (
        <ScrollView
          style={styles.scrollFull}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {songs.map((song, index) => renderFullSongCard(song, index))}
        </ScrollView>
      )}
      <TouchableOpacity
        style={styles.fullFooterAdd}
        onPress={() => pushSongs([...songs, createEmptySong(true)])}
        accessibilityLabel={labels.addItemA11y}
      >
        <Ionicons name="add-circle-outline" size={20} color={theme.brand.primary} />
        <Text style={styles.fullFooterAddText}>{labels.addItem}</Text>
      </TouchableOpacity>
    </View>
  );
}

export function GuestListEditor(props) {
  return <SetlistEditor {...props} labels={GUEST_LIST_LABELS} />;
}

export function ContactsEditor(props) {
  return <SetlistEditor {...props} labels={CONTACTS_LABELS} />;
}

export function StructuredDayListEditor({ tabKey, ...props }) {
  const labels = STRUCTURED_DAY_LIST_LABELS[tabKey] || SETLIST_LABELS;
  return <SetlistEditor {...props} labels={labels} />;
}

function createStyles(
  theme,
  compact,
  fullMaxHeight,
  compactFloatHost,
  compactFill = false,
  compactUnscroll = false,
  fullUnscroll = false,
  dense = false
) {
  const d = (regular, denseVal) => (dense ? denseVal : regular);
  const footerReserve = 72;
  const scrollFullMax =
    compact ? 200 : typeof fullMaxHeight === 'number' ? Math.max(120, fullMaxHeight - footerReserve) : 480;
  const scrollCompactStyle = compactUnscroll
    ? { width: '100%', minWidth: 0 }
    : compactFill
      ? { flex: 1, minHeight: 160 }
      : dense
        ? { flex: 1, minHeight: 0 }
        : { maxHeight: compact ? (compactFloatHost ? 72 : 280) : 240 };
  return StyleSheet.create({
    wrap: {
      width: '100%',
      minWidth: 0,
      ...(dense && !compactUnscroll ? { flex: 1, minHeight: 0 } : {}),
      ...(compactFill && !compactUnscroll ? { flex: 1, minHeight: 0 } : {}),
      ...(compact && !compactFloatHost && !compactFill && !compactUnscroll ? { overflow: 'hidden' } : {}),
      ...(typeof fullMaxHeight === 'number' && !compact && !fullUnscroll
        ? { maxHeight: fullMaxHeight, overflow: 'hidden' }
        : {}),
    },
    compactFloatingHost: {
      position: 'relative',
      overflow: 'visible',
      width: '100%',
      zIndex: 2,
    },
    compactFloatingAnchor: { width: '100%', overflow: 'visible' },
    compactComposerFloat: {
      position: 'absolute',
      bottom: 0,
      width: 240,
      maxWidth: 260,
      zIndex: 50,
      ...(Platform.OS === 'android' ? { elevation: 10 } : {}),
      ...(Platform.OS === 'web'
        ? { boxShadow: `0 4px 20px ${theme.ui.shadowStrong || 'rgba(0,0,0,0.18)'}` }
        : {}),
    },
    compactComposerFloatRight: { left: '100%', marginLeft: 6 },
    compactComposerFloatLeft: { right: '100%', marginRight: 6 },
    scrollCompact: scrollCompactStyle,
    scrollCompactUnscroll: { width: '100%', minWidth: 0 },
    scrollFull: { maxHeight: fullUnscroll ? undefined : scrollFullMax },
    scrollFullUnscroll: { width: '100%', minWidth: 0 },
    compactRowWrap: { marginBottom: d(8, 5), flexDirection: 'row', alignItems: 'stretch', gap: d(8, 5) },
    compactRow: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: d(10, 6),
      paddingVertical: d(10, 5),
      paddingHorizontal: d(12, 7),
      borderRadius: d(10, 8),
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
      minHeight: d(52, 34),
    },
    compactRowActive: {
      borderColor: theme.brand.primary,
      backgroundColor: theme.name === 'dark' ? 'rgba(46, 204, 113, 0.12)' : 'rgba(39, 174, 96, 0.1)',
    },
    compactIndexBadge: {
      width: d(28, 20),
      height: d(28, 20),
      borderRadius: d(14, 10),
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.card,
      borderWidth: 1,
      borderColor: theme.ui.border,
      flexShrink: 0,
    },
    compactIndexText: {
      fontSize: d(12, 10),
      fontWeight: '800',
      color: theme.brand.primary,
    },
    compactRowBody: { flex: 1, minWidth: 0, gap: 2 },
    compactPreview: { fontSize: d(14, 11), fontWeight: '700', lineHeight: d(19, 15), color: theme.text.primary },
    compactSubtitle: { fontSize: d(12, 10), lineHeight: d(16, 13), color: theme.text.secondary },
    compactDeleteBtn: {
      width: d(34, 24),
      height: d(34, 24),
      borderRadius: d(17, 12),
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
      flexShrink: 0,
      alignSelf: 'center',
    },
    compactPlusBtnInline: {
      alignSelf: 'center',
      marginTop: 4,
      marginBottom: 4,
      width: 20,
      height: 20,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.brand.primary,
      backgroundColor: theme.background.card,
    },
    compactComposerCard: {
      marginTop: d(8, 4),
      marginBottom: d(10, 6),
      paddingHorizontal: d(12, 7),
      paddingVertical: d(12, 7),
      borderRadius: d(10, 8),
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    compactComposerCardFloating: {
      marginTop: 0,
      marginBottom: 0,
    },
    denseComposerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 5,
      paddingVertical: 5,
    },
    denseComposerInput: {
      flex: 1,
      minWidth: 0,
      marginBottom: 0,
    },
    denseComposerIconBtn: {
      width: 24,
      height: 24,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
      flexShrink: 0,
    },
    denseComposerIconBtnPrimary: {
      borderColor: theme.button.primary,
      backgroundColor: theme.button.primary,
    },
    compactTitleInput: {
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: d(10, 7),
      paddingVertical: d(10, 5),
      fontSize: d(15, 12),
      fontWeight: '700',
      color: theme.input.text,
      backgroundColor: theme.input.background,
      marginBottom: d(10, 6),
    },
    compactComposerActions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      marginTop: 8,
      gap: 6,
    },
    compactComposerActionsFlex: { flex: 1, minWidth: 4 },
    compactComposerBtnPrimary: {
      paddingHorizontal: 12,
      paddingVertical: 5,
      borderRadius: 7,
      backgroundColor: theme.button.primary,
    },
    compactComposerBtnPrimaryText: { fontSize: 11, fontWeight: '800', color: theme.button.primaryText },
    compactComposerBtnGhost: { paddingHorizontal: 8, paddingVertical: 5 },
    compactComposerBtnGhostText: { fontSize: 11, fontWeight: '700', color: theme.text.secondary },
    compactComposerBtnDanger: { paddingHorizontal: 8, paddingVertical: 5 },
    compactComposerBtnDangerText: { fontSize: 11, fontWeight: '800', color: theme.status?.error || '#c0392b' },
    songCard: {
      marginBottom: 10,
      paddingHorizontal: 12,
      paddingVertical: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    songHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    fullReorderCol: { flexDirection: 'column', justifyContent: 'center' },
    fullReorderBtn: { paddingVertical: 2, paddingHorizontal: 2, minWidth: 24, alignItems: 'center' },
    fullReorderBtnDisabled: { opacity: 0.28 },
    songToggleBtn: {
      width: 28,
      height: 28,
      borderRadius: 7,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    songTitleInput: {
      flex: 1,
      minWidth: 0,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      fontSize: 15,
      fontWeight: '700',
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    songDeleteBtn: { padding: 4 },
    songDetailsBlock: {
      marginTop: 10,
      paddingTop: 8,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      gap: 6,
    },
    detailLineRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    detailField: {
      flex: 1,
      minWidth: 0,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: d(10, 7),
      paddingVertical: d(8, 5),
      fontSize: d(13, 11),
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    detailFieldPrimary: { flex: 1.1 },
    detailFieldSecondary: { flex: 1 },
    detailExpandBtn: {
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.brand.primary,
      backgroundColor: theme.background.card,
    },
    detailCollapseBtn: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    detailRemoveBtn: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    addDetailLineBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      marginTop: 2,
      paddingVertical: 4,
    },
    addDetailLineText: { fontSize: 12, fontWeight: '700', color: theme.brand.primary },
    fullFooterAdd: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      marginTop: 10,
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderStyle: 'dashed',
      backgroundColor: theme.background.card,
    },
    fullFooterAddText: { fontSize: 14, fontWeight: '800', color: theme.brand.primary },
  });
}
